import { isEmpty } from 'radash';

import { useLayout } from '@/components/datamodel/hooks/useLayout';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getItemTitleFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { PersistedItem } from '@/types/DataModel';

interface UseItemTitleProps {
    isCreatingNewItem: boolean;
    baseItemData?: PersistedItem;
}

export const useItemTitle = ({ isCreatingNewItem, baseItemData }: UseItemTitleProps) => {
    const { layout } = useLayout();
    const { t } = useTranslation();
    const {user} = useUser();
    const {organization} = useOrganization();
    let customTitleFilter: ReturnType<typeof getItemTitleFilter> | undefined;

    if (!layout?.layoutOptions?.itemTitleFilter) {
        logger.warn('Editor layout has no title filter defined, it\'s recommended that you use a title filter instead of the unfriendly primary key.');
    } else {
        customTitleFilter = getItemTitleFilter(layout.layoutOptions.itemTitleFilter);
        if (!customTitleFilter) {
            logger.error(`Custom filter with name ${layout.layoutOptions.itemTitleFilter} was not found.`);
        }
    }

    // By default, we use the item primary key as a title
    let title = '';
    if (customTitleFilter && !isCreatingNewItem && baseItemData) {
        try {
            title = customTitleFilter(baseItemData, user, organization);
            if (isEmpty(title)) {
                title = baseItemData.primaryKey__;
            }
        } catch (error) {
            logger.error(`Custom filter with name ${layout?.layoutOptions?.itemTitleFilter} is return an error`, error);
        }
    } else if (isCreatingNewItem) {
        title = t('CREATE_NEW_ITEM');
    }
    return { title };
};
